/**
 * @enable2/api-types
 *
 * Shared TypeScript API types for Enable 2.0, PFA's Patient Financial Advocacy
 * Platform. These interfaces mirror the public HTTP contracts documented in
 * `docs/contracts/api-contracts.md` and the entity shapes in
 * `docs/architecture/data-model.md`.
 *
 * Both frontends (`apps/staff-web` and `apps/patient-app`) import from this
 * package so request/response shapes do not drift between them. This module is
 * types-only — it contains no runtime logic beyond shared string constants.
 */

// ---------------------------------------------------------------------------
// Versioning
// ---------------------------------------------------------------------------

/** API version path prefix. All public routes are mounted under this prefix. */
export const API_V1 = '/api/v1' as const;

// ---------------------------------------------------------------------------
// Roles (FND-005 role matrix)
// ---------------------------------------------------------------------------

/**
 * Staff role union used for RBAC and role-scoped work queues.
 * Maps to the FND-005 role matrix (subset exposed to the frontends).
 */
export type Role = 'staff_admin' | 'pas' | 'bas' | 'finance' | 'retention';

// ---------------------------------------------------------------------------
// Error envelope (api-contracts.md § Error Model)
// ---------------------------------------------------------------------------

/** A single structured sub-error (e.g. a per-field validation failure). */
export interface ApiErrorDetail {
  /** Field the sub-error applies to, when relevant. */
  field?: string;
  /** Machine/human-readable reason for the sub-error. */
  reason?: string;
  /** Arbitrary additional context (e.g. required_consent, from/to, retry_after_seconds). */
  [key: string]: unknown;
}

/** The platform error envelope returned for all 4xx/5xx responses. */
export interface ApiError {
  error: {
    /** Stable, machine-readable error code (e.g. `VALIDATION_ERROR`, `CONSENT_REQUIRED`). */
    code: string;
    /** Human-readable summary, safe to log; never contains PHI. */
    message: string;
    /** Structured sub-errors; may be empty. */
    details: ApiErrorDetail[];
    /** Correlates to App Insights / Log Analytics for support and audit. */
    trace_id: string;
  };
}

// ---------------------------------------------------------------------------
// Pagination (api-contracts.md § Pagination)
// ---------------------------------------------------------------------------

/** Cursor-pagination metadata returned alongside list results. */
export interface PageInfo {
  /** Opaque, tenant-scoped cursor for the next page; `null` when `has_more` is false. */
  next_cursor: string | null;
  /** Whether further pages exist. */
  has_more: boolean;
  /** Page size that was applied. */
  limit: number;
}

/**
 * Generic cursor-paginated list envelope.
 *
 * The wire format wraps items under `data` with a `page` block, but this
 * generic provides the simplified `{ items, next_cursor }` projection the
 * frontends consume after unwrapping. See {@link PagedResponse} for the raw
 * envelope shape.
 */
export interface Page<T> {
  items: T[];
  next_cursor?: string;
}

/** Raw list-response envelope as emitted on the wire. */
export interface PagedResponse<T> {
  data: T[];
  page: PageInfo;
}

// ---------------------------------------------------------------------------
// Shared scalars
// ---------------------------------------------------------------------------

/** ISO-8601 timestamp string (e.g. `2026-06-20T09:15:00Z`). */
export type IsoDateTime = string;

/** ISO-8601 date string (e.g. `2026-07-01`). */
export type IsoDate = string;

/** Preferred patient language; drives localization (EN/ES). */
export type LanguagePref = 'en' | 'es';

// ---------------------------------------------------------------------------
// Domain DTOs
// ---------------------------------------------------------------------------

/**
 * Case — the central operational entity, scoped to a tenant (hospital) and a
 * patient (api-contracts.md § Case API, data-model.md § `case`).
 */
export interface Case {
  id: string;
  tenant_id: string;
  patient_id: string;
  /** FK → employer; nullable until matched. */
  employer_id?: string | null;
  /** Current phase-based stage. */
  stage: string;
  /** Status within the current stage. */
  status: string;
  /** Computed by the rules engine, with factor breakdown. */
  priority_score?: number;
  /** From the finance rule set. */
  recommended_effective_date?: IsoDate | null;
  /** Current owner. */
  assigned_user_id?: string | null;
  /** Structured fields collected at the current stage. */
  stage_data?: Record<string, unknown>;
  created_at: IsoDateTime;
  updated_at: IsoDateTime;
}

/**
 * CaseAttempt — a structured contact attempt logged against a case
 * (api-contracts.md § POST /cases/{id}/attempts).
 */
export interface CaseAttempt {
  attempt_id: string;
  case_id: string;
  /** Contact method used for the attempt. */
  contact_method: 'in_person' | 'hospital_phone' | 'ringcentral';
  /** Structured outcome code (e.g. `no_answer_left_vm`). */
  outcome_code: string;
  /** Scheduled next action, when one was set. */
  next_action?: {
    type: string;
    scheduled_at?: IsoDateTime;
  };
  /** Structured free-form notes. */
  notes_structured?: Record<string, unknown>;
  /** User id that logged the attempt. */
  logged_by: string;
  created_at: IsoDateTime;
}

/**
 * Patient — PHI-bearing subject of a case (data-model.md § `patient`).
 * Frontends generally receive masked/projected views; PHI fields are optional.
 */
export interface Patient {
  id: string;
  tenant_id: string;
  /** Medical record number (PHI). */
  mrn?: string;
  /** Full name (PHI); may be masked for display. */
  name?: string;
  /** Date of birth (PHI). */
  dob?: IsoDate;
  /** Demographics blob (PHI). */
  demographics?: Record<string, unknown>;
  /** Contact details — phone/email/address (PHI). */
  contact?: Record<string, unknown>;
  /** Preferred language; drives localization. */
  language_pref?: LanguagePref;
}

/** Employer contact card (api-contracts.md § Employer Match API). */
export interface EmployerContact {
  /** Contact name; may be masked. */
  name: string;
  /** Role of the contact (e.g. `benefits`). */
  role?: string;
  /** Contact email (PII). */
  email?: string;
  /** Contact phone (PII). */
  phone?: string;
}

/**
 * Employer — matched employer record (api-contracts.md § Employer Match API,
 * data-model.md § `employer`).
 */
export interface Employer {
  employer_id: string;
  name: string;
  /** Fuzzy-match score from the type-ahead search, when returned by search. */
  match_score?: number;
  /** Whether employer-sponsored insurance is available. */
  esi_available?: boolean;
  /** Preferred enrollment method (e.g. `portal`). */
  preferred_enrollment_method?: string;
  /** Most-recently-validated benefit package summary. */
  benefit_package?: BenefitPackage;
  /** Employer contacts. */
  contacts?: EmployerContact[];
}

/**
 * BenefitPackage — recency-validated benefit package attached to an employer/case
 * (api-contracts.md § Employer Match API, data-model.md § `benefit_package`).
 */
export interface BenefitPackage {
  /** When the package recency was last validated. */
  recency_validated_at?: IsoDate | null;
  /** Source of validation (e.g. `employer_email`). */
  source?: string;
}

/** A single invoice line item (api-contracts.md § Invoice API). */
export interface InvoiceLine {
  /** Line id, present once persisted. */
  line_id?: string;
  /** Source charge id. */
  charge_id?: string;
  amount: number;
  /** Rate source (e.g. `medicaid_tx`). */
  rate_source?: string;
}

/**
 * Invoice — draft/approved invoice generated from synced charges
 * (api-contracts.md § Invoice API, data-model.md § Revenue chain).
 */
export interface Invoice {
  invoice_id: string;
  case_id: string;
  status: 'draft' | 'approved' | 'submitted' | string;
  total: number;
  lines?: InvoiceLine[];
  approved_by?: string;
  approved_at?: IsoDateTime;
}

/**
 * ConsentRecord — TCPA / HIPAA / e-sign consent evidence
 * (api-contracts.md § Consent API, data-model.md § `consent_record`).
 */
export interface ConsentRecord {
  consent_id: string;
  case_id: string;
  /** Type of consent captured. */
  consent_type: 'tcpa' | 'hipaa' | 'e-sign' | string;
  /** How the consent was captured (e.g. `in_app`, `iPad`, `SMS link`). */
  channel?: string;
  /** E-signature vendor reference. */
  signature_ref?: string;
  /** ADLS Gen2 evidence artifact URI. */
  evidence_blob_uri?: string;
  /** Consent text version (e.g. `2026-01`). */
  version: string;
  /** When the consent was captured. */
  captured_at?: IsoDateTime;
}

/** Human-in-the-loop disposition for an AI interaction. */
export type HitlStatus = 'auto_accepted' | 'pending_review' | 'reviewed' | string;

/**
 * AIInteraction — a single logged AI gateway call
 * (api-contracts.md § AI API, data-model.md § `ai_interaction`).
 */
export interface AIInteraction {
  ai_interaction_id: string;
  /** Invoked capability (e.g. `doc-classification`, `propensity-score`). */
  capability: string;
  /** Advisory output; absent when routed to HITL before producing output. */
  output?: Record<string, unknown>;
  /** Model confidence score. */
  confidence: number;
  /** HITL disposition. */
  hitl_status: HitlStatus;
  /** Bound model identifier (e.g. `doc-intel-v2`). */
  model_binding_id?: string;
  /** Whether the call was disclosure-gated for the tenant. */
  disclosure_gated?: boolean;
  /** Review task id, present when routed to HITL. */
  review_task_id?: string;
}

/** Delivery channel for a notification/message. */
export type NotificationChannel = 'sms' | 'email' | 'in_app' | 'push' | string;

/**
 * Notification — an outbound message/notification record
 * (data-model.md § Notification/Message/DeliveryEvent).
 */
export interface Notification {
  id: string;
  tenant_id: string;
  /** Optional case association. */
  case_id?: string;
  /** Delivery channel. */
  channel: NotificationChannel;
  /** Delivery status (e.g. `queued`, `sent`, `delivered`, `failed`, `blocked`). */
  status: string;
  /** Notification template/type identifier. */
  type?: string;
  /** Rendered or templated body, when surfaced to the frontend. */
  body?: string;
  created_at: IsoDateTime;
}

/** A next-step prompt surfaced to the patient (api-contracts.md § Patient App API). */
export interface PatientNextStep {
  /** Step type (e.g. `upload_document`). */
  type: string;
  /** Document type requested, when the step is an upload. */
  doc_type?: string;
  /** Due-by date for the step. */
  due_by?: IsoDate;
}

/**
 * PatientStatus — the patient-facing projection of their case
 * (api-contracts.md § GET /patient/status). No internal staff fields.
 */
export interface PatientStatus {
  /** Patient-facing case reference. */
  case_ref: string;
  /** Friendly, localized stage label. */
  stage_label: string;
  /** Friendly status label. */
  status_label: string;
  /** Ordered next steps for the patient. */
  next_steps: PatientNextStep[];
  /** Language the labels are rendered in. */
  language: LanguagePref;
}

/** Classification status for an uploaded patient document. */
export interface PatientDocumentClassification {
  /** Classification status (e.g. `pending`, `classified`, `failed`). */
  status: string;
  /** Pipeline that performed/queued classification (e.g. `ai_gateway`). */
  via?: string;
  /** Resolved document type once classified. */
  doc_type?: string;
}

/**
 * PatientDocument — a document uploaded by a patient via guided capture
 * (api-contracts.md § POST /patient/documents, data-model.md § Document).
 */
export interface PatientDocument {
  patient_document_id: string;
  /** Patient-facing case reference. */
  case_ref: string;
  /** Upload/processing status (e.g. `received`). */
  status: string;
  /** Declared or classified document type. */
  doc_type?: string;
  /** Classification outcome. */
  classification?: PatientDocumentClassification;
}
